import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { Trophy, Lock, ArrowLeft, Shield, Star, Flame, Zap, Gamepad2, Users, Sparkles, CreditCard } from "lucide-react";
import { Progress } from "../components/ui/progress";

const CATEGORY_ICONS = {
  course: Star,
  xp: Zap,
  mastery: Trophy,
  game: Gamepad2,
  streak: Flame,
  community: Users,
  special: Sparkles,
  purchase: CreditCard,
};

const CATEGORY_COLORS = {
  course: "text-[#F7931A]",
  xp: "text-yellow-400",
  mastery: "text-orange-500",
  game: "text-[#00E5FF]",
  streak: "text-red-400",
  community: "text-green-400",
  special: "text-purple-400",
  purchase: "text-[#F7931A]",
};

export default function Achievements() {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/achievements/all").then(res => {
      setAchievements(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categories = ["all", ...new Set((achievements?.achievements || []).map(a => a.category))];
  const filtered = filter === "all"
    ? achievements?.achievements || []
    : (achievements?.achievements || []).filter(a => a.category === filter);

  const earnedPct = achievements ? (achievements.earned_count / achievements.total_count) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="achievements-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">ACHIEVEMENTS</h1>
          <span className="badge-orange font-mono">{achievements?.earned_count}/{achievements?.total_count}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Progress */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="card-dark p-6" data-testid="achievements-progress"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-white font-medium">Collection Progress</span>
            <span className="text-[#F7931A] font-mono">{Math.round(earnedPct)}%</span>
          </div>
          <Progress value={earnedPct} className="h-3" />
        </motion.div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filter === cat
                  ? "bg-[#F7931A] text-black"
                  : "bg-[#161616] text-zinc-400 border border-white/10 hover:border-[#F7931A]/50"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat === "all" ? "All" : cat === "xp" ? "BTC XP" : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((badge, i) => {
            const IconComp = CATEGORY_ICONS[badge.category] || Trophy;
            const colorClass = CATEGORY_COLORS[badge.category] || "text-[#F7931A]";
            return (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.03 }}
                className={`card-dark p-4 text-center ${badge.earned ? "border-[#F7931A]/40" : "opacity-50"}`}
                data-testid={`badge-${badge.id}`}
              >
                <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                  badge.earned ? "bg-[#F7931A]/20" : "bg-zinc-800"
                }`}>
                  {badge.earned ? (
                    <IconComp className={colorClass} size={24} />
                  ) : (
                    <Lock className="text-zinc-600" size={20} />
                  )}
                </div>
                <p className={`text-sm font-medium ${badge.earned ? "text-white" : "text-zinc-600"}`}>
                  {badge.name}
                </p>
                <p className="text-xs text-zinc-500 mt-1">{badge.description}</p>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
